// A member's status from the office (BL-056): suspend (no renewal and no member prices, its visits
// still due) and resume; cancel today or from a later date, refunding what is unused by the plan's
// rule as a credit on their account, refunded from Money; and skip a due visit. Each is
// memberships.suspend, .resume, .cancel and .skipVisit; while the company mirrors ServiceTitan the
// server refuses them, in words.

import { useState } from 'react';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { errorText, Money, useQuery } from '../books/ui.tsx';
import { customerHref } from '../history/customer-link.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import {
  CANCEL_REFUND_WORDS,
  cancelDays,
  SALE_WORDS,
  IMPORTED_REFUND_NOTE,
  refundAsked,
  suspendNote,
  unusedCents,
  type CancelRefund,
  type MembershipDetail,
} from './model.ts';

interface StatusOut {
  status: string;
  creditCents: number;
  creditId: string | null;
  message: string;
}

/** Suspend… and Cancel… (or Resume), for a membership that is still current. */
export function MemberStatusActions({
  detail: d,
  name,
  today,
  onChanged,
}: {
  detail: MembershipDetail;
  /** "Ana Ruiz’s Comfort Club membership". */
  name: string;
  today: string;
  /** After a change: what was said, to read the membership again. */
  onChanged: (said: { message: string; customerId?: string; creditCents?: number }) => void;
}) {
  const [open, setOpen] = useState<'suspend' | 'cancel' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (d.status !== 'active' && d.status !== 'suspended') return null;
  const resume = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<StatusOut>('memberships.resume', { membershipId: d.id });
      onChanged({ message: r.message });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (open === 'suspend')
    return <SuspendForm detail={d} name={name} onClose={() => setOpen(null)} onDone={onChanged} />;
  if (open === 'cancel')
    return (
      <CancelForm detail={d} name={name} today={today} onClose={() => setOpen(null)} onDone={onChanged} />
    );
  return (
    <div className="row member-status-actions">
      {d.status === 'active' && !d.cancelsOn && (
        <button type="button" className="btn soft" onClick={() => setOpen('suspend')}>
          Suspend…
        </button>
      )}
      {d.status === 'suspended' && (
        <button type="button" className="btn soft" disabled={busy} onClick={() => void resume()}>
          {busy ? 'Resuming…' : 'Resume'}
        </button>
      )}
      {!d.cancelsOn && (
        <button type="button" className="btn soft cancel-member" onClick={() => setOpen('cancel')}>
          Cancel…
        </button>
      )}
      {error && <p className="error small">{error}</p>}
    </div>
  );
}

function SuspendForm({
  detail: d,
  name,
  onClose,
  onDone,
}: {
  detail: MembershipDetail;
  name: string;
  onClose: () => void;
  onDone: (said: { message: string }) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const save = async () => {
    setTried(true);
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<StatusOut>('memberships.suspend', {
        membershipId: d.id,
        reason: reason.trim(),
      });
      onDone({ message: r.message });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="fgrid member-status-form"
      aria-label={`Suspend ${name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <p className="wide small">
        While it is suspended, it doesn’t renew and member prices don’t apply. Its visits still come due and
        can be booked. {suspendNote(d.endsOn, dayText)}
      </p>
      <label className="wide">
        <span className="flabel">Why</span>
        <input
          className="field"
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Card declined, away for the winter…"
        />
      </label>
      {tried && !reason.trim() && (
        <p className="error small wide" role="alert">
          Say why it is suspended.
        </p>
      )}
      <div className="row-actions wide">
        <button type="button" className="btn soft" onClick={onClose}>
          Back
        </button>
        <SaveButton busy={busy} busyLabel="Suspending…" error={error} onClick={() => void save()}>
          Suspend it
        </SaveButton>
      </div>
    </form>
  );
}

function CancelForm({
  detail: d,
  name,
  today,
  onClose,
  onDone,
}: {
  detail: MembershipDetail;
  name: string;
  today: string;
  onClose: () => void;
  onDone: (said: { message: string; customerId?: string; creditCents?: number }) => void;
}) {
  // the plan's rule at a cancel, read now: it is the company's policy, not the sale's
  const plans = useQuery<{ plans: { id: string; cancelRefund?: CancelRefund }[] }>(
    'membershipTypes.list',
    {},
  );
  const planRule: CancelRefund = plans.data?.plans.find((p) => p.id === d.typeId)?.cancelRefund ?? 'optional';
  const unused = unusedCents(d);
  // a sale voided (or still a draft) defers nothing: there is nothing to refund, whatever the rule
  const saleSays = d.sale ? SALE_WORDS[d.sale] : undefined;
  const rule: CancelRefund = saleSays ? 'never' : planRule;
  const days = cancelDays(today, d.endsOn);
  const [on, setOn] = useState(today);
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState((unused / 100).toFixed(2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const asked = refundAsked(rule, refund, unused);
  const later = on > today;
  const problems = [
    ...(!reason.trim() ? ['Say why it is cancelled.'] : []),
    ...(on < days.min || on > days.max ? [`A day from ${dayText(days.min)} to ${dayText(days.max)}.`] : []),
    ...(asked.problem ? [asked.problem] : []),
  ];
  const save = async () => {
    setTried(true);
    if (problems.length || !plans.data) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<StatusOut>('memberships.cancel', {
        membershipId: d.id,
        reason: reason.trim(),
        ...(later ? { effectiveOn: on } : {}),
        ...(asked.cents !== undefined ? { refundCents: asked.cents } : {}),
      });
      onDone({ message: r.message, customerId: d.customerId, creditCents: r.creditCents });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="fgrid member-status-form"
      aria-label={`Cancel ${name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <label>
        <span className="flabel">From</span>
        <input
          className="field"
          type="date"
          min={days.min}
          max={days.max}
          value={on}
          onChange={(e) => setOn(e.target.value)}
        />
      </label>
      <label className="wide">
        <span className="flabel">Why</span>
        <input
          className="field"
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Moving away, not using it…"
        />
      </label>
      <p className="wide small">
        It stops renewing now.
        {later ? ` On ${dayText(on)}` : ' Today'} its visits stop, and a visit job booked from that day on
        that hasn’t started is cancelled.
      </p>
      <div className="wide cancel-balance">
        <p className="small">
          <Money cents={unused} /> of <Money cents={d.deferredCents} /> paid for the term isn’t used yet
          (before tax).
          <span className="muted small block">Plan rule: {CANCEL_REFUND_WORDS[planRule]}.</span>
        </p>
        {saleSays ? (
          <p className="notice warn small">{saleSays}</p>
        ) : rule === 'optional' ? (
          <label>
            <span className="flabel">Refund as a credit on their account</span>
            <input
              className="field"
              inputMode="decimal"
              value={refund}
              onChange={(e) => setRefund(e.target.value)}
            />
            <span className="muted small block">
              At most <Money cents={unused} />
              {later ? ', or what is unused by then' : ''}. The rest is kept as income.
            </span>
          </label>
        ) : rule === 'always' ? (
          <p className="small">
            <Money cents={unused} /> {later ? '(or what is unused by then) ' : ''}is refunded as a credit on
            their account.
          </p>
        ) : (
          <p className="small">Nothing is refunded: what is unused is kept as income.</p>
        )}
        {!d.invoiceId && rule !== 'never' && <p className="muted small">{IMPORTED_REFUND_NOTE}</p>}
        {rule !== 'never' && (
          <p className="muted small">
            The credit is refunded from their Money tab. A sale still owing takes it off what is owed first.
          </p>
        )}
      </div>
      {tried && problems.length > 0 && (
        <ul className="error small wide" role="alert">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div className="row-actions wide">
        <button type="button" className="btn soft" onClick={onClose}>
          Back
        </button>
        {d.sale !== 'draft' && (
          <SaveButton busy={busy} busyLabel="Cancelling…" error={error} onClick={() => void save()}>
            {later ? `Cancel it from ${dayText(on)}` : 'Cancel it today'}
          </SaveButton>
        )}
      </div>
    </form>
  );
}

/** What a change said, with the way to the credit when one was left on their account. */
export function StatusSaid({
  said,
}: {
  said: { message: string; customerId?: string; creditCents?: number } | null;
}) {
  if (!said) return null;
  return (
    <p className="notice ok small member-status-said" role="status">
      {said.message}
      {said.creditCents && said.customerId ? (
        <>
          {' '}
          <a href={customerHref(said.customerId, 'money')}>Open their Money tab</a>
        </>
      ) : null}
    </p>
  );
}

/** Skip… on a due visit: counted as used and its share recognised, the next one comes due. */
export function SkipVisit({
  recurringServiceId,
  what,
  onDone,
}: {
  recurringServiceId: string;
  /** "Ana Ruiz’s tune-up due Nov 20". */
  what: string;
  onDone: (said: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const skip = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ nextDueOn: string }>('memberships.skipVisit', { recurringServiceId });
      onDone(`Skipped ${what}: it counts as used. The next one is due ${dayText(r.nextDueOn)}.`);
      setAsking(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!asking)
    return (
      <button type="button" className="btn quiet" aria-label={`Skip ${what}`} onClick={() => setAsking(true)}>
        Skip…
      </button>
    );
  return (
    <span className="skip-ask" role="group" aria-label={`Skip ${what}?`}>
      <span className="small block">It counts as used, as if done.</span>
      <button type="button" className="btn soft" disabled={busy} onClick={() => void skip()}>
        {busy ? 'Skipping…' : 'Skip it'}
      </button>{' '}
      <button type="button" className="btn quiet" onClick={() => setAsking(false)}>
        Back
      </button>
      {error && <span className="error small block">{error}</span>}
    </span>
  );
}
